import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _pick from "lodash/pick";
import _snakeCase from "lodash/snakeCase";
// Polymorph keys
export var POLYMORPH = ["model_type", "model_id"];
// Special controller actions
export var ACTIONS = ["config", "count"];

/**
 * Create a functions that checks all keys of an object with the given
 * fn against a reference object and combines results with the given
 * arrFn (e.g. some/every).
 * @param {(k:string) => (a:object) => (b:object) => boolean} fn
 * @param {string} arrFn
 * @returns {(b:object) => boolean}
 */
export var matcher = function matcher(fn) {
  var arrFn = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "every";
  return function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return function (a) {
      var fns = args.map(function (v) {
        return (typeof v === "function" ? v : fn(v))(a);
      });
      return function (b) {
        return fns[arrFn](function (f) {
          return f(b);
        });
      };
    };
  };
};

/**
 * Matcher that requires all values to match
 */
export var matchAll = matcher(function (v) {
  return function (a) {
    return function (b) {
      return a[v] === b[v];
    };
  };
});
/**
 * Matcher that requires at least one value to match
 */
export var matchOne = matcher(function (v) {
  return function (a) {
    return function (b) {
      return a[v] && a[v] === b[v];
    };
  };
}, "some");
/**
 * Matcher that requires the given values to either not be set or at  least
 * as high as on the reference object.
 */
export var matchMin = matcher(function (v) {
  return function (a) {
    return function (b) {
      return !b[v] || a[v] && a[v] <= b[v];
    };
  };
});
/**
 * Matcher that checks all polymorph keys
 */
export var matchModel = matchAll.apply(void 0, POLYMORPH);
/**
 * Matcher that checks all action keys
 */
export var matchAction = matchAll.apply(void 0, ACTIONS);
/**
 * Matcher that checks all keys that uniquely define an entitiy
 */
export var matchEntitiy = matchAll(matchModel, matchAction);
/**
 * Mathcer that checks all keys that uniquely define a loading state
 */
export var matchLoaded = matchAll(matchEntitiy, matchMin("limit", "top"));

/**
 * Convert to Integer if possible
 * @param {any} v
 */
export var toInt = function toInt(v) {
  return typeof v === "boolean" ? v && 1 || 0 : !isNaN(parseInt(v)) ? parseInt(v) : v;
};

/**
 * Generate a random CID
 * @returns {string}
 */
export var cid = function cid() {
  return Math.random().toString(36).substring(2);
};

/**
 * Convert an object to a valid args object
 */
export var toArgs = function toArgs(args) {
  return Object.fromEntries(Object.entries(args).map(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
      k = _ref2[0],
      v = _ref2[1];
    return [_snakeCase(k), parseInt(v) || k === "top" && v && 3 || toInt(v)];
  }).filter(function (_ref3) {
    var _ref4 = _slicedToArray(_ref3, 2),
      v = _ref4[1];
    return v || v === 0;
  }));
};

/**
 * Just because we are lazy
 * @param {string} model_type
 * @param {int} model_id
 * @param  {object} args
 */
export var getArgs = function getArgs(model_type, model_id) {
  for (var _len2 = arguments.length, args = new Array(_len2 > 2 ? _len2 - 2 : 0), _key2 = 2; _key2 < _len2; _key2++) {
    args[_key2 - 2] = arguments[_key2];
  }
  return toArgs(args.filter(function (v) {
    return typeof v === "object";
  }).reduce(function (a, v) {
    return _objectSpread(_objectSpread({}, a), v);
  }, {
    model_type: model_type,
    model_id: model_id
  }));
};

/**
 * Only the args that define an entitiy
 * @param {object} args
 */
export var entityArgs = function entityArgs(args) {
  return _pick.apply(void 0, [args].concat(POLYMORPH, ACTIONS));
};

/**
 * Build a query string from an args object
 * @param {object} args
 * @returns {string}
 */
export var toQuery = function toQuery(args) {
  args = toArgs(args);
  return Object.keys(args).sort().map(function (k) {
    return typeof args[k] === "object" ? Object.entries(args[k]).map(function (_ref5) {
      var _ref6 = _slicedToArray(_ref5, 2),
        i = _ref6[0],
        v = _ref6[1];
      return "".concat(k, "[").concat(i, "]=").concat(v);
    }).join("&") : "".concat(k, "=").concat(args[k]);
  }).join("&");
};

/**
 * Get an args object from a query string
 * @param {string} q
 * @returns {object}
 */
export var fromQuery = function fromQuery(q) {
  return Object.fromEntries(q.split("&").map(function (v) {
    return v.split("=");
  }).map(function (_ref7) {
    var _ref8 = _slicedToArray(_ref7, 2),
      k = _ref8[0],
      v = _ref8[1];
    return [k, toInt(v)];
  }));
};
//# sourceMappingURL=common.js.map